<template>
  <li
    :class="[
      'nav__item',
      { 'nav__item--active': model.active, 'nav__item--active-parent': hasActiveChildren },
    ]"
  >
    <a
      :key="model.id"
      :class="{
        nav__link: 'nav__link',
        'nav__link--active': checkActive(model),
      }"
      :href="model.href"
      tabindex="0"
      @click.prevent.stop="model.type === 'routerLink' ? onNavigate(model) : toggleChildren()"
      @keydown.enter="model.type === 'routerLink' ? onNavigate(model) : toggleChildren()"
    >
      <span v-chakra="{ m: '0 1rem' }">
        <c-icon
          v-if="model.icon"
          :name="model.icon"
          :color="checkActive(model) || hasActiveChildren ? 'brand.purple' : 'brand.purpleLight'"
          size="1.75rem"
        />
      </span>
      <span class="nav__link__inner--active" />
      <c-text
        as="span"
        class="nav__link__inner"
        :title="model.name"
        flex="1"
        textOverflow="ellipsis"
        overflow="hidden"
        whiteSpace="nowrap"
        :color="checkActive(model) || hasActiveChildren ? 'brand.purple' : 'gray.700'"
      >
        {{ model.name }}
      </c-text>
      <span v-if="model.count > 0" class="nav__item__count">({{ model.count }})</span>
      <span v-if="model.children && model.children.length > 0">
        <c-icon
          v-if="model.icon"
          :name="isActive() ? 'chevron_up' : 'chevron_down'"
          :color="checkActive(model) || hasActiveChildren ? 'brand.purple' : 'brand.purpleLight'"
          size="1rem"
        />
      </span>
    </a>
    <span
      v-if="hasActiveChildren || model.active"
      class="nav__item__active-parent-marker"
      v-chakra="
        (theme) => ({
          position: 'absolute',
          top: '10px',
          left: '-18px',
          bottom: 0,
          width: '30px',
          height: '30px',
          borderRightWidth: '15px',
          borderRightColor: theme.colors.brand.purple,
          borderRadius: theme.radii.full,
          textAlign: 'center',
          cursor: 'pointer',
        })
      "
    />
    <transition name="nav__item__anim--slide">
      <ul v-if="hasChildren" v-show="hasActiveChildren || open" class="nav__children">
        <nav-item
          v-for="item in model.children"
          :key="item.id"
          :model="item"
          @navigate="onNavigate"
        ></nav-item>
      </ul>
    </transition>
  </li>
</template>

<script>
import Icon from "./Icon.vue";
import { CIcon, CText } from "@chakra-ui/vue";

export default {
  name: "NavItem",
  hideFromDocs: true, // For some reason this gets hidden from the navigation even when listed in docs.config.js
  components: {
    Icon,
    CIcon,
    CText,
  },
  props: {
    model: {
      required: true,
      type: Object,
    },
    type: {
      type: String,
      default: "ul",
      validator: (value) => {
        return value.match(/(ul|div)/);
      },
    },
  },
  data() {
    return {
      open: this.model.children?.some(({ active }) => active === true),
      children: false,
    };
  },
  computed: {
    hasChildren: function () {
      return this.model.children?.length;
    },
    hasActiveChildren: function () {
      return this.model.children?.some(({ active }) => active === true);
    },
  },
  methods: {
    isActive: function () {
      return this.model.children?.some(({ active }) => active === true) || this.open;
    },
    toggleChildren: function () {
      if (this.hasChildren) {
        this.open = !this.open;
      }
    },
    onNavigate: function (model) {
      this.$emit("navigate", model);
    },
    checkActive: function (model) {
      return model.active;
    },
  },
};
</script>

<style lang="scss">
@import "../../design-system/assets/tokens/tokens";

.nav__item {
  position: relative;
  overflow: hidden;
  margin: 0;
  padding: 0;
  list-style-type: none;
  font-size: 14px;
  text-align: left;
  .nav__link {
    display: flex;
    align-items: center;
    padding: 14px 16px 14px 16px;
    color: $text-color;
    text-decoration: none;
    cursor: pointer;

    @include media-min-width("lg") {
      padding: 14px 28px;
    }
  }
  &__count {
    color: #b4b4b4;
    font-size: 12px;
    margin: 0 $space-xs;
  }
  // Vue Transition Animations
  &__anim--slide-enter-active {
    transition-duration: 0.2s;
    transition-timing-function: linear;
  }
  &__anim--slide-leave-active {
    transition-duration: 0.2s;
    transition-timing-function: ease-out;
  }
  &__anim--slide-enter-to,
  &__anim--slide-leave {
    max-height: 400px;
    width: 100%;
    overflow: hidden;
  }
  &__anim--slide-enter,
  &__anim--slide-leave-to {
    overflow: hidden;
    max-height: 0;
    width: 100%;
  }
}
// Sub levels
.nav__children {
  display: block;
  margin: 0;
  padding: 3px 0 8px 0;
  .nav__item {
    margin: 0 0;
    font-size: 14px;
    .nav__link {
      border: 0;
      padding: 9px 24px 9px 48px;

      @include media-min-width("lg") {
        padding: 9px 24px 9px 60px;
      }

      &--active {
        position: relative;
        background: transparent;
        color: $color-iloq-blue-dark;

        .nav__link__inner--active {
          position: relative;

          &::before {
            content: "";
            top: calc(50% - 1px);
            left: -2px;
            position: absolute;
            height: 2px;
            width: 6px;
            background: $color-iloq-blue-dark;

            @include media-min-width("lg") {
              width: 12px;
            }
          }
        }
      }
      &__inner {
        display: inline-block;
        margin-left: 10px;
      }
    }
    &__active-parent-marker {
      display: none;
    }
  }
}

@include media-min-width("lg") {
  .nav__children {
    .nav__item {
      .nav__link {
        &__inner {
          margin-left: 20px;
        }
      }
    }
  }
}
</style>
